import { createAsyncThunk } from '@reduxjs/toolkit';
import { uniq } from 'lodash';
import moment from 'moment';
import { SALARIES_TRANSFER_SEND } from 'scenes/Permissions/constants/permissions';
import { selectIsRequiredBusinessPermissionFromCurrentActiveEmployerBusiness } from 'scenes/Permissions/store/selectors/personalPermissionsSelectors';
import { generateReportFromRecords } from 'scenes/WorkDemo/utils/report-generation/generateReportFromRecords';
import { selectAssignmentsArray } from 'store/assignments/selectors/assignment.selectors';
import { returnAssignmentsWithFilledAssignmentNumberPath } from 'store/assignments/utils/returnAssignmentsWithFillerAssignmentNumberPath.util';
import { selectProjectUsersArray } from 'store/projectUsers/selectors/projectUsers.selectors';
import { loadEmployeeConfigurationsInBusiness } from 'store/projectUsers/thunks/loadEmployeeConfigurationsInBusiness.thunk';
import { selectSelectedProject } from 'store/projects/selectors/projectsSelectors';
import { selectRecordAllowancesGroupedByRecordId } from 'store/recordAllowances/selectors/record-and-employer-allowances.selectors';
import { selectSelectedRecordsIds } from 'store/records/selectors/records-base.selectors';
import { selectWorkCalendarAllowanceTypeRecordsGroupedByParentRecordId } from 'store/records/selectors/records.selectors';
import {
  selectIsAutomaticMealBreakReductionsAppliedToRecordBasedReductions,
  selectIsIncludeOnlyApprovedRecordsEnabled,
  selectIsRecordBasedReductionsActive,
  selectIsReportProjectHourRowsEnabled,
  selectReportGeneratedFromMode,
} from 'store/reports/selectors/reports.selectors';
import { selectCurrentUserActiveEmployerProject } from 'store/resources/selectors/resources.selectors';
import { selectShadowRecordEntities } from 'store/shadowData/selectors/shadowData.selectors';
import { getShadowProjectsByIds } from 'store/shadowData/services/getShadowProjectsByIds.service';
import {
  selectUserActiveEmployerBusinessId,
  selectUserActiveEmployerInfo,
} from 'store/user/selectors.js/user.selectors';
import { selectWorkCalendarEmployersArray } from '../../selectors/base-work-calendar.selectors';
import {
  selectProjectsDataResourcesArrayWithEmployeeConfigurationInformation,
  selectWorkCalendarFilteredRecords,
  selectWorkCalendarProjectsArray,
} from '../../selectors/workSelectorsDemo';

export const generateReportPreviewTableFromSelectedRecords = createAsyncThunk(
  'workCalendar/generateReportPreviewTableFromSelectedRecords',
  async (dialogSettings, { getState, dispatch }) => {
    const selectedProject = selectSelectedProject(getState());

    const selectedRecordIds = selectSelectedRecordsIds(getState());

    const reportGeneratedFromMode = selectReportGeneratedFromMode(getState());

    const projects = selectWorkCalendarProjectsArray(getState());

    const employerProjects = selectWorkCalendarEmployersArray(getState());

    const allWorkCalendarFilteredRecords =
      selectWorkCalendarFilteredRecords(getState());

    const assignments = selectAssignmentsArray(getState());

    const users = selectProjectUsersArray(getState());

    const isRecordBasedReductions =
      selectIsRecordBasedReductionsActive(getState());

    const isReportProjectHourRowsEnabled =
      selectIsReportProjectHourRowsEnabled(getState());

    const isPermissionToSendSalaries =
      selectIsRequiredBusinessPermissionFromCurrentActiveEmployerBusiness(
        getState(),
        SALARIES_TRANSFER_SEND
      );

    const recordAllowancesGroupedByRecordId =
      selectRecordAllowancesGroupedByRecordId(getState());

    const utcOffset = moment.parseZone(moment().format()).format('Z');

    const allowanceRecordsGroupedByParentRecordId =
      selectWorkCalendarAllowanceTypeRecordsGroupedByParentRecordId(getState());

    const isIncludeOnlyApprovedRecordsEnabled =
      selectIsIncludeOnlyApprovedRecordsEnabled(getState());

    const activeEmployerProject =
      selectCurrentUserActiveEmployerProject(getState());

    const isAutomaticMealBreakReductionsAppliedToRecordBasedReductions =
      selectIsAutomaticMealBreakReductionsAppliedToRecordBasedReductions(
        getState()
      );

    const automaticMealBreakReductionLengthMinutes =
      activeEmployerProject?.automaticMealBreakReductionLengthMinutes
        ? activeEmployerProject.automaticMealBreakReductionLengthMinutes
        : 30;

    const automaticMealBreakReductionLengthHours =
      automaticMealBreakReductionLengthMinutes / 60;

    const {
      selectedReportType,
      workStartTime,
      workEndTime,
      fromDate,
      toDate,
      language,
      filetype,
      workHourType,
      paidCoffeeBreakMinutes,
    } = dialogSettings;

    const generationParams = {
      fromDate: moment(fromDate).utcOffset(0, true).format(),
      toDate: moment(toDate).utcOffset(0).format(),
      selectedReportType,
      workStartTime,
      workEndTime,
      language,
      filetype,
      utcOffset,
      workHourType,
      isRecordBasedReductions,
      paidCoffeeBreakMinutes,
      isProjectHourRowsEnabled: isReportProjectHourRowsEnabled,
      automaticMealBreakReductionLengthHours,
      isAutomaticMealBreakReductionsAppliedToRecordBasedReductions,
    };

    if (isPermissionToSendSalaries) {
      await dispatch(loadEmployeeConfigurationsInBusiness());
    }

    const resources =
      selectProjectsDataResourcesArrayWithEmployeeConfigurationInformation(
        getState()
      );

    const projectsWithEmployers = [...projects, ...employerProjects];

    const recordsToGenerateReportFrom =
      returnEffectiveRecordsToGenerateReportFrom(
        allWorkCalendarFilteredRecords,
        selectedRecordIds,
        reportGeneratedFromMode,
        isIncludeOnlyApprovedRecordsEnabled
      );

    let shadowRecordsMap = {};
    let shadowProjectsMap = {};
    const activeEmployerBusinessId =
      selectUserActiveEmployerBusinessId(getState());

    const assignmentsWithFullAssignmentNumberPath =
      returnAssignmentsWithFilledAssignmentNumberPath(assignments);

    if (
      selectedReportType === 'jydacom_salary_report' &&
      activeEmployerBusinessId
    ) {
      const shadowRecordEntities = selectShadowRecordEntities(getState());

      recordsToGenerateReportFrom.forEach((record) => {
        const shadowRecordId = `${record.id}_${activeEmployerBusinessId}`;
        const shadowRecord = shadowRecordEntities[shadowRecordId];
        if (shadowRecord) {
          shadowRecordsMap[shadowRecordId] = shadowRecord;
        }
      });

      const activeEmployerInfo = selectUserActiveEmployerInfo(getState());
      const uniqueProjectIds = uniq(
        recordsToGenerateReportFrom.map((record) => record.project)
      );

      const shadowProjectIds = uniqueProjectIds.map(
        (projectId) => `${projectId}_${activeEmployerBusinessId}`
      );

      try {
        const shadowProjects = await getShadowProjectsByIds(
          shadowProjectIds,
          activeEmployerInfo
        );

        (shadowProjects || []).forEach((shadowProject) => {
          if (shadowProject) {
            shadowProjectsMap[shadowProject.id] = shadowProject;
          }
        });
      } catch (error) {}
    }

    const report = await generateReportFromRecords(
      recordsToGenerateReportFrom,
      resources,
      assignmentsWithFullAssignmentNumberPath,
      users,
      generationParams,
      projectsWithEmployers,
      selectedProject,
      recordAllowancesGroupedByRecordId,
      allowanceRecordsGroupedByParentRecordId,
      shadowRecordsMap,
      shadowProjectsMap,
      activeEmployerBusinessId
    );

    return report;
  }
);

const returnEffectiveRecordsToGenerateReportFrom = (
  records,
  selectedRecordIds,
  reportGeneratedFromMode,
  isIncludeOnlyApprovedRecordsEnabled
) => {
  const reportRecords =
    reportGeneratedFromMode === 'SELECTED'
      ? records.filter((record) => selectedRecordIds.includes(record.id))
      : records;

  const recordsToReturn = isIncludeOnlyApprovedRecordsEnabled
    ? reportRecords.filter((record) => record.approved)
    : reportRecords;

  return recordsToReturn;
};
